import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  BarChart3,
  User,
  Wallet,
  Receipt,
  Sliders,
  Lightbulb,
  CheckCircle2,
  Languages,
  ArrowRight,
  Shield,
  Zap,
  FileDown,
  Globe,
  Sparkles,
  Building2,
  Gauge,
  TrendingUp,
  Menu,
  X,
  Star,
  CreditCard,
} from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { cn } from "../lib/utils";

const copy = {
  fr: {
    navFeatures: "Fonctionnalités",
    navHow: "Comment ça marche",
    navWhy: "Pourquoi BudgetLens",
    navFaq: "FAQ",
    launch: "Lancer l'outil",
    signIn: "Se connecter",
    heroBadge: "Nouveau — Conforme HCSF 35%",
    heroRating: "Note 4,9 / 5",
    heroRatingSub: "240+ courtiers",
    heroSecure: "Sécurité bancaire",
    heroSecureSub: "Confidentiel",
    heroNoCC: "Sans carte",
    heroNoCCSub: "Accès immédiat",
    heroTitle1: "Vivez pleinement",
    heroTitle2: "l'expérience BudgetLens.",
    heroTitle3: "",
    heroSubtitle:
      "BudgetLens vous fait gagner du temps grâce à des diagnostics uniques. Nous vous donnons les clés pour accompagner vos clients vers le succès financier qu'ils méritent.",
    heroTagline: "Le diagnostic budgétaire de vos clients en 2 minutes.",
    ctaPrimary: "Essayer gratuitement",
    ctaSecondary: "Voir la démo",
    heroStatLabel1: "Indicateurs calculés",
    heroStatLabel2: "Modules intégrés",
    heroStatLabel3: "Conformité HCSF",
    heroStatValue1: "12+",
    heroStatValue2: "6",
    heroStatValue3: "35%",
    featuresEyebrow: "Fonctionnalités",
    featuresTitle: "Tout ce qu'il faut pour un bilan pro.",
    featuresSubtitle:
      "Six modules pensés pour accompagner le conseiller de la collecte de données au rendu client.",
    f1t: "Fiche client",
    f1d: "Collectez les informations patrimoniales et contextualisez le dossier en un instant.",
    f2t: "Revenus",
    f2d: "Neuf types de revenus — salaires, BIC, BNC, dividendes, CAF, pensions… — sommes automatiques.",
    f3t: "Dépenses",
    f3d: "Charges fixes, crédits en cours et dépenses variables : sous-totaux et total en temps réel.",
    f4t: "Analyse",
    f4d: "Reste à vivre, taux d'endettement, capacité d'emprunt, diagnostic 🟢 🟠 🔴.",
    f5t: "Optimisation",
    f5d: "Simulez les économies potentielles avec des curseurs 0–50% / 0–30%.",
    f6t: "Recommandations",
    f6d: "Alertes et opportunités générées automatiquement selon les seuils métier.",
    howEyebrow: "Comment ça marche",
    howTitle: "Trois étapes. Zéro friction.",
    how1t: "Saisissez",
    how1d: "Renseignez revenus et dépenses dans des formulaires guidés — les totaux se calculent à la frappe.",
    how2t: "Analysez",
    how2d: "Le diagnostic visuel met en évidence endettement, reste à vivre et capacité d'emprunt.",
    how3t: "Exportez",
    how3d: "Générez un rapport PDF complet à remettre à votre client en un clic.",
    whyEyebrow: "Pourquoi BudgetLens",
    whyTitle: "Conçu pour les courtiers exigeants.",
    w1t: "Confidentiel",
    w1d: "Aucune donnée envoyée à un serveur. Vos clients restent vos clients.",
    w2t: "Temps réel",
    w2d: "Pas de bouton « Calculer ». Les indicateurs se mettent à jour instantanément.",
    w3t: "Export pro",
    w3d: "Rapport PDF mis en page, prêt à signer ou envoyer par email.",
    w4t: "Bilingue FR / EN",
    w4d: "Basculez la langue d'un clic pour vos clients internationaux.",
    testimonialsEyebrow: "Témoignages",
    testimonialsTitle: "Ils ont remplacé Excel par BudgetLens.",
    t1q: "Mes rendez-vous bilan sont passés de 1 h à 25 minutes. Le client repart avec un PDF clair, c'est game-changer.",
    t1n: "Sophie L.",
    t1r: "Courtière en crédit",
    t1c: "Cabinet Méridien",
    t2q: "Le diagnostic visuel rouge/orange/vert est immédiatement compris par les clients. Plus besoin de longues explications.",
    t2n: "Karim B.",
    t2r: "Conseiller en gestion de patrimoine",
    t2c: "Atlas Patrimoine",
    t3q: "Enfin un outil qui calcule la capacité d'emprunt en temps réel pendant que je saisis. Indispensable.",
    t3n: "Élodie P.",
    t3r: "Courtière indépendante",
    t3c: "EP Conseil",
    faqEyebrow: "FAQ",
    faqTitle: "Questions fréquentes",
    q1: "Mes données sont-elles sécurisées ?",
    a1: "Oui. Aucune donnée n'est transmise à un serveur externe : la confidentialité de vos clients est garantie.",
    q2: "Puis-je l'utiliser hors ligne ?",
    a2: "Oui. Après le premier chargement, BudgetLens reste utilisable même sans connexion internet.",
    q3: "Quels seuils sont utilisés pour le diagnostic ?",
    a3: "Nous appliquons le taux HCSF de 35% pour l'endettement et un reste à vivre minimum de 800€ — modifiables dans le code pour vos propres normes internes.",
    q4: "Est-ce gratuit ?",
    a4: "Oui, la version actuelle est entièrement gratuite et sans inscription.",
    finalTitle: "Prêt à simplifier vos bilans ?",
    finalSubtitle:
      "Ouvrez l'outil, saisissez un dossier test et générez votre premier rapport en moins de 3 minutes.",
    footerTagline: "L'outil de diagnostic budgétaire moderne pour conseillers.",
    footerProduct: "Produit",
    footerCompany: "Ressources",
    footerLegal: "Légal",
    footerRights: "Tous droits réservés.",
    promiseEyebrow: "Notre promesse",
    promiseTitle1: "Vivez pleinement l'expérience",
    promiseTitleAccent: "BudgetLens.",
    promiseBody:
      "BudgetLens vous fait gagner du temps grâce à des diagnostics précis et instantanés. Nous vous donnons les clés pour accompagner vos clients avec l'excellence qu'ils méritent.",
    promiseStat1: "2 min",
    promiseStat1Sub: "pour un bilan complet",
    promiseStat2: "0 €",
    promiseStat2Sub: "aucun engagement",
    promiseStat3: "100%",
    promiseStat3Sub: "conforme HCSF",
    promiseCta: "Commencer maintenant",
  },
  en: {
    navFeatures: "Features",
    navHow: "How it works",
    navWhy: "Why BudgetLens",
    navFaq: "FAQ",
    launch: "Launch the tool",
    signIn: "Sign in",
    heroBadge: "New — HCSF 35% compliant",
    heroRating: "Rated 4.9 / 5",
    heroRatingSub: "240+ brokers",
    heroSecure: "Bank-grade",
    heroSecureSub: "Confidential",
    heroNoCC: "No card",
    heroNoCCSub: "Instant access",
    heroTitle1: "Live the full",
    heroTitle2: "BudgetLens experience.",
    heroTitle3: "",
    heroSubtitle:
      "BudgetLens saves you time with unique diagnostics. We give you the keys to guide your clients toward the financial success they deserve.",
    heroTagline: "Your clients' budget diagnosis in 2 minutes.",
    ctaPrimary: "Try it free",
    ctaSecondary: "See the demo",
    heroStatLabel1: "Computed metrics",
    heroStatLabel2: "Integrated modules",
    heroStatLabel3: "HCSF compliance",
    heroStatValue1: "12+",
    heroStatValue2: "6",
    heroStatValue3: "35%",
    featuresEyebrow: "Features",
    featuresTitle: "Everything you need for a pro review.",
    featuresSubtitle:
      "Six modules designed to guide the advisor from data intake to the client-ready report.",
    f1t: "Client profile",
    f1d: "Gather wealth information and contextualize the file in an instant.",
    f2t: "Income",
    f2d: "Nine types of income — salaries, BIC, BNC, dividends, allowances, pensions… — auto-summed.",
    f3t: "Expenses",
    f3d: "Fixed charges, loans and variable expenses: subtotals and total in real time.",
    f4t: "Analysis",
    f4d: "Disposable income, debt ratio, borrowing capacity, 🟢 🟠 🔴 diagnosis.",
    f5t: "Optimization",
    f5d: "Simulate potential savings with 0–50% / 0–30% sliders.",
    f6t: "Recommendations",
    f6d: "Alerts and opportunities generated automatically from business thresholds.",
    howEyebrow: "How it works",
    howTitle: "Three steps. Zero friction.",
    how1t: "Input",
    how1d: "Fill in income and expenses in guided forms — totals update as you type.",
    how2t: "Analyse",
    how2d: "The visual diagnosis highlights debt, disposable income and borrowing capacity.",
    how3t: "Export",
    how3d: "Generate a complete PDF report for your client in one click.",
    whyEyebrow: "Why BudgetLens",
    whyTitle: "Built for demanding brokers.",
    w1t: "Confidential",
    w1d: "No data sent to a server. Your clients stay yours.",
    w2t: "Real time",
    w2d: "No 'Compute' button. Metrics refresh instantly.",
    w3t: "Pro export",
    w3d: "Paginated PDF report, ready to sign or email.",
    w4t: "Bilingual FR / EN",
    w4d: "Flip the language in one click for your international clients.",
    testimonialsEyebrow: "Testimonials",
    testimonialsTitle: "They swapped Excel for BudgetLens.",
    t1q: "My review meetings dropped from 1h to 25 minutes. Clients leave with a clear PDF — it's a game changer.",
    t1n: "Sophie L.",
    t1r: "Mortgage broker",
    t1c: "Méridien Firm",
    t2q: "The red/amber/green visual diagnosis is instantly understood by clients. No more long explanations.",
    t2n: "Karim B.",
    t2r: "Wealth management advisor",
    t2c: "Atlas Wealth",
    t3q: "Finally a tool that computes borrowing capacity in real time as I type. Essential.",
    t3n: "Élodie P.",
    t3r: "Independent broker",
    t3c: "EP Conseil",
    faqEyebrow: "FAQ",
    faqTitle: "Frequently asked",
    q1: "Is my data safe?",
    a1: "Yes. No data is transmitted to any external server — your clients' privacy is guaranteed.",
    q2: "Can I use it offline?",
    a2: "Yes. Once loaded, BudgetLens works even without an internet connection.",
    q3: "Which thresholds power the diagnosis?",
    a3: "We apply the HCSF 35% debt ratio and a minimum disposable income of €800 — both tunable in code for your internal standards.",
    q4: "Is it free?",
    a4: "Yes, the current version is entirely free and requires no sign-up.",
    finalTitle: "Ready to simplify your reviews?",
    finalSubtitle:
      "Open the tool, enter a test file and generate your first report in under 3 minutes.",
    footerTagline: "The modern budget diagnostic tool for advisors.",
    footerProduct: "Product",
    footerCompany: "Resources",
    footerLegal: "Legal",
    footerRights: "All rights reserved.",
    promiseEyebrow: "Our promise",
    promiseTitle1: "Live the full",
    promiseTitleAccent: "BudgetLens experience.",
    promiseBody:
      "BudgetLens saves you time with precise, instant diagnostics. We give you the keys to serve your clients with the excellence they deserve.",
    promiseStat1: "2 min",
    promiseStat1Sub: "for a full review",
    promiseStat2: "€0",
    promiseStat2Sub: "no commitment",
    promiseStat3: "100%",
    promiseStat3Sub: "HCSF compliant",
    promiseCta: "Start now",
  },
};

const FEATURES = [
  { icon: User, tk: "f1t", dk: "f1d", tint: "indigo" },
  { icon: Wallet, tk: "f2t", dk: "f2d", tint: "emerald" },
  { icon: Receipt, tk: "f3t", dk: "f3d", tint: "rose" },
  { icon: Gauge, tk: "f4t", dk: "f4d", tint: "amber" },
  { icon: Sliders, tk: "f5t", dk: "f5d", tint: "sky" },
  { icon: Lightbulb, tk: "f6t", dk: "f6d", tint: "violet" },
];

const TINT = {
  indigo: "bg-indigo-50 text-indigo-700",
  emerald: "bg-emerald-50 text-emerald-700",
  rose: "bg-rose-50 text-rose-700",
  amber: "bg-amber-50 text-amber-700",
  sky: "bg-sky-50 text-sky-700",
  violet: "bg-violet-50 text-violet-700",
};

const WHY = [
  { icon: Shield, tk: "w1t", dk: "w1d" },
  { icon: Zap, tk: "w2t", dk: "w2d" },
  { icon: FileDown, tk: "w3t", dk: "w3d" },
  { icon: Globe, tk: "w4t", dk: "w4d" },
];

const Section = ({ id, eyebrow, title, subtitle, children }) => (
  <section id={id} className="max-w-6xl mx-auto px-5 sm:px-8 py-20 md:py-28" data-testid={`section-${id}`}>
    <div className="flex flex-col items-start gap-3 mb-10 md:mb-14">
      <span className="text-xs font-bold uppercase tracking-[0.22em] text-indigo-700/80">{eyebrow}</span>
      <h2
        className="text-3xl sm:text-4xl md:text-5xl tracking-tighter text-indigo-950 max-w-3xl"
        style={{ fontFamily: "Outfit, sans-serif" }}
      >
        {title}
      </h2>
      {subtitle ? <p className="text-base md:text-lg text-slate-600 max-w-2xl">{subtitle}</p> : null}
    </div>
    {children}
  </section>
);

const Navbar = () => {
  const { lang, toggleLang } = useI18n();
  const c = copy[lang];
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#features", label: c.navFeatures },
    { href: "#how", label: c.navHow },
    { href: "#why", label: c.navWhy },
    { href: "#faq", label: c.navFaq },
  ];

  return (
    <header
      data-testid="landing-navbar"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_2px_20px_rgba(0,0,0,0.04)]"
          : "bg-transparent"
      )}
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="landing-logo">
          <div className={cn(
            "w-9 h-9 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform",
            scrolled ? "bg-indigo-950" : "bg-white/10 border border-white/15 backdrop-blur"
          )}>
            <BarChart3 className={cn("w-4 h-4", scrolled ? "text-white" : "text-white")} />
          </div>
          <span
            className={cn(
              "text-lg font-medium tracking-tight transition-colors",
              scrolled ? "text-indigo-950" : "text-white"
            )}
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            BudgetLens
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className={cn(
                "text-sm transition-colors font-medium",
                scrolled
                  ? "text-slate-600 hover:text-indigo-950"
                  : "text-white/75 hover:text-white"
              )}
              data-testid={`nav-link-${l.href.slice(1)}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <button
            data-testid="btn-lang-toggle-landing"
            onClick={toggleLang}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all font-medium",
              scrolled
                ? "text-slate-600 hover:text-indigo-950 hover:bg-slate-100/80"
                : "text-white/80 hover:text-white hover:bg-white/10"
            )}
            aria-label="Toggle language"
          >
            <Languages className="w-4 h-4" />
            <span className="uppercase tracking-wider text-xs">{lang}</span>
          </button>
          <Link
            to="/app"
            data-testid="btn-launch-app"
            className={cn(
              "flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-all shadow-sm hover:shadow-md active:scale-95",
              scrolled
                ? "bg-indigo-950 text-white hover:bg-indigo-900"
                : "bg-white text-indigo-950 hover:bg-emerald-300"
            )}
          >
            {c.launch}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <button
          data-testid="btn-mobile-menu"
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "md:hidden p-2 rounded-lg",
            scrolled ? "text-indigo-950 hover:bg-slate-100" : "text-white hover:bg-white/10"
          )}
          aria-label="Menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open ? (
        <div className="md:hidden bg-white border-t border-slate-200 px-5 py-4 flex flex-col gap-3" data-testid="mobile-menu">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-sm text-slate-700 hover:text-indigo-950 font-medium"
            >
              {l.label}
            </a>
          ))}
          <div className="flex items-center gap-2 pt-3 border-t border-slate-200">
            <button
              onClick={toggleLang}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 border border-slate-200"
            >
              <Languages className="w-4 h-4" />
              {lang.toUpperCase()}
            </button>
            <Link
              to="/app"
              className="flex-1 flex items-center justify-center gap-1.5 bg-indigo-950 text-white rounded-xl px-4 py-2 text-sm font-medium"
            >
              {c.launch}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      ) : null}
    </header>
  );
};

const TrustStrip = () => {
  const { lang } = useI18n();
  const c = copy[lang];
  const items = [
    {
      iconWide: true,
      icon: (
        <div className="flex items-center gap-0.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
          ))}
        </div>
      ),
      t: c.heroRating,
      s: c.heroRatingSub,
    },
    { icon: <Shield className="w-4 h-4 text-emerald-300" />, t: c.heroSecure, s: c.heroSecureSub },
    { icon: <CreditCard className="w-4 h-4 text-amber-300" />, t: c.heroNoCC, s: c.heroNoCCSub },
  ];
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-10"
      data-testid="hero-trust-strip"
    >
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
          <div
            className={cn(
              "h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0",
              it.iconWide ? "px-2" : "w-8"
            )}
          >
            {it.icon}
          </div>
          <div className="flex flex-col leading-tight min-w-0">
            <span className="text-sm font-medium text-white whitespace-nowrap">{it.t}</span>
            <span className="text-xs text-white/50 whitespace-nowrap">{it.s}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const ProductPreview = ({ lang }) => (
  <div className="relative w-full">
    {/* Glow halos */}
    <div
      aria-hidden
      className="absolute -inset-10 -z-10 opacity-70 pointer-events-none"
      style={{
        backgroundImage:
          "radial-gradient(45% 35% at 30% 20%, rgba(99,102,241,0.45), transparent 70%), radial-gradient(40% 30% at 80% 70%, rgba(16,185,129,0.30), transparent 70%)",
      }}
    />

    {/* Main app card */}
    <div className="relative rounded-3xl bg-white border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.5)] overflow-hidden">
      {/* App chrome */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-200 bg-slate-50">
        <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <span className="ml-3 text-[11px] text-slate-400 font-medium tracking-wide">
          budgetlens.app
        </span>
      </div>

      <div className="p-6 flex flex-col gap-5">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">
              {lang === "fr" ? "Analyse" : "Analysis"}
            </div>
            <h3
              className="text-xl tracking-tighter text-indigo-950 mt-0.5"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {lang === "fr" ? "Diagnostic budgétaire" : "Budget diagnosis"}
            </h3>
          </div>
          <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="text-[11px] font-medium text-emerald-700">
              {lang === "fr" ? "Saine" : "Healthy"}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          {[
            { l: lang === "fr" ? "Revenus" : "Income", v: "5 880 €", c: "text-emerald-700" },
            { l: lang === "fr" ? "Reste à vivre" : "Disposable", v: "2 840 €", c: "text-emerald-700" },
            { l: lang === "fr" ? "Endettement" : "Debt ratio", v: "15,31 %", c: "text-indigo-950" },
            { l: lang === "fr" ? "Capacité" : "Capacity", v: "277 920 €", c: "text-indigo-950" },
          ].map((k, i) => (
            <div key={i} className="rounded-xl bg-slate-50 border border-slate-200 p-3">
              <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">{k.l}</div>
              <div
                className={cn("text-lg font-medium mt-1", k.c)}
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {k.v}
              </div>
            </div>
          ))}
        </div>

        {/* Mini chart bars */}
        <div className="flex flex-col gap-2">
          {[
            { l: lang === "fr" ? "Endettement" : "Debt", v: 31, color: "bg-emerald-500" },
            { l: lang === "fr" ? "Charges fixes" : "Fixed charges", v: 22, color: "bg-indigo-500" },
            { l: lang === "fr" ? "Variables" : "Variables", v: 14, color: "bg-amber-400" },
          ].map((b, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-[10px] text-slate-500 w-20 truncate">{b.l}</span>
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={cn("h-full rounded-full", b.color)}
                  style={{ width: `${(b.v / 35) * 100}%` }}
                />
              </div>
              <span className="text-[10px] text-slate-500 tabular-nums w-8 text-right">{b.v}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Floating chip — top right */}
    <div
      className="hidden md:flex absolute -top-5 -right-5 items-center gap-2 bg-white rounded-2xl shadow-xl border border-slate-200 px-3 py-2 animate-in fade-in slide-in-from-top-2 duration-700"
      style={{ animationDelay: "200ms" }}
    >
      <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
        <TrendingUp className="w-3.5 h-3.5 text-emerald-700" />
      </div>
      <div className="leading-tight pr-1">
        <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">
          {lang === "fr" ? "Capacité" : "Capacity"}
        </div>
        <div
          className="text-sm font-medium text-indigo-950 tabular-nums"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          277 920 €
        </div>
      </div>
    </div>

    {/* Floating chip — bottom left */}
    <div
      className="hidden md:flex absolute -bottom-6 -left-6 items-center gap-3 bg-white rounded-2xl shadow-xl border border-slate-200 px-4 py-3 animate-in fade-in slide-in-from-bottom-2 duration-700"
      style={{ animationDelay: "400ms" }}
    >
      <div className="w-9 h-9 rounded-lg bg-indigo-950 text-white flex items-center justify-center">
        <FileDown className="w-4 h-4" />
      </div>
      <div className="leading-tight">
        <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">
          {lang === "fr" ? "Rapport" : "Report"}
        </div>
        <div
          className="text-sm font-medium text-indigo-950"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {lang === "fr" ? "PDF prêt" : "PDF ready"}
        </div>
      </div>
    </div>
  </div>
);

const Hero = () => {
  const { lang } = useI18n();
  const c = copy[lang];
  return (
    <section className="relative pt-24 md:pt-28 pb-20 overflow-hidden bg-indigo-950" data-testid="hero">
      {/* Ambient bg */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(60% 50% at 15% 0%, rgba(99,102,241,0.45), transparent 70%), radial-gradient(50% 40% at 90% 30%, rgba(16,185,129,0.18), transparent 70%), radial-gradient(50% 40% at 80% 100%, rgba(251,191,36,0.10), transparent 70%)",
        }}
      />
      {/* Grid pattern */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          maskImage:
            "radial-gradient(70% 60% at 50% 30%, #000 20%, transparent 80%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center pt-12">
        {/* Left — pitch */}
        <div className="lg:col-span-7 flex flex-col">
          <div
            className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full pl-2 pr-4 py-1 w-fit mb-7"
            data-testid="hero-badge"
          >
            <span className="w-5 h-5 rounded-full bg-emerald-400 flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-indigo-950" />
            </span>
            <span className="text-xs font-medium text-white/90">{c.heroBadge}</span>
          </div>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tighter text-white leading-[1.02]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {c.heroTitle1}
            <br />
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-r from-emerald-300 via-emerald-400 to-amber-300 bg-clip-text text-transparent">
                {c.heroTitle2}
              </span>
            </span>
          </h1>

          <p className="mt-6 text-base md:text-lg text-white/70 max-w-xl leading-relaxed">
            {c.heroSubtitle}
          </p>

          <p
            className="mt-5 text-sm md:text-base text-emerald-300/90 font-medium tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {c.heroTagline}
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link
              to="/app"
              data-testid="hero-cta-primary"
              className="group flex items-center gap-2 bg-white text-indigo-950 hover:bg-emerald-300 rounded-xl px-6 py-3.5 text-base font-semibold transition-all shadow-lg active:scale-95"
            >
              {c.ctaPrimary}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <a
              href="#how"
              data-testid="hero-cta-secondary"
              className="flex items-center gap-2 text-white/85 hover:text-white border border-white/20 hover:border-white/40 rounded-xl px-6 py-3.5 text-base font-medium transition-all"
            >
              {c.ctaSecondary}
            </a>
          </div>

          <TrustStrip />
        </div>

        {/* Right — product preview */}
        <div className="lg:col-span-5 relative" data-testid="hero-product-preview">
          <ProductPreview lang={lang} />
        </div>
      </div>
    </section>
  );
};

const MockPreview = () => {
  const { lang } = useI18n();
  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 pb-10">
      <div className="relative rounded-3xl border border-white/60 bg-white/50 backdrop-blur-xl shadow-[0_20px_60px_rgba(30,27,75,0.15)] overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-200/70 bg-white/60">
          <span className="w-3 h-3 rounded-full bg-rose-400" />
          <span className="w-3 h-3 rounded-full bg-amber-400" />
          <span className="w-3 h-3 rounded-full bg-emerald-400" />
          <span className="ml-3 text-xs text-slate-500">budgetlens.app / {lang === "fr" ? "analyse" : "analysis"}</span>
        </div>
        <div className="grid grid-cols-4 min-h-[360px]">
          <div className="col-span-1 bg-white/70 border-r border-slate-200/70 p-5 hidden md:flex flex-col gap-2">
            {["Client", "Revenus", "Dépenses", "Analyse", "Optimisation"].map((l, i) => (
              <div
                key={l}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-lg text-xs",
                  i === 3 ? "bg-indigo-950 text-white" : "text-slate-600"
                )}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                {l}
              </div>
            ))}
          </div>
          <div className="col-span-4 md:col-span-3 p-6 md:p-8 flex flex-col gap-6">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Analyse</span>
              <h3 className="text-2xl tracking-tighter text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
                {lang === "fr" ? "Diagnostic budgétaire" : "Budget diagnosis"}
              </h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { l: lang === "fr" ? "Revenus" : "Income", v: "5 880 €", c: "text-emerald-700" },
                { l: lang === "fr" ? "Reste à vivre" : "Disposable", v: "2 840 €", c: "text-emerald-700" },
                { l: lang === "fr" ? "Endettement" : "Debt ratio", v: "15,31 %", c: "text-emerald-700" },
                { l: lang === "fr" ? "Capacité" : "Capacity", v: "277 920 €", c: "text-indigo-950" },
              ].map((k, i) => (
                <div key={i} className="rounded-xl bg-white/70 border border-slate-200/70 p-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{k.l}</div>
                  <div className={cn("text-xl font-medium mt-1", k.c)} style={{ fontFamily: "Outfit, sans-serif" }}>
                    {k.v}
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-700" />
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                  {lang === "fr" ? "Diagnostic global" : "Overall diagnosis"}
                </div>
                <div className="text-lg text-emerald-800 font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {lang === "fr" ? "Situation saine" : "Healthy situation"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Features = () => {
  const { lang } = useI18n();
  const c = copy[lang];
  return (
    <Section id="features" eyebrow={c.featuresEyebrow} title={c.featuresTitle} subtitle={c.featuresSubtitle}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {FEATURES.map(({ icon: Icon, tk, dk, tint }) => (
          <div
            key={tk}
            data-testid={`feature-${tk}`}
            className="group bg-white/70 backdrop-blur-xl border border-white/60 rounded-2xl p-6 md:p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(30,27,75,0.08)]"
          >
            <div className={cn("w-11 h-11 rounded-xl flex items-center justify-center mb-5", TINT[tint])}>
              <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-xl font-medium text-indigo-950 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              {c[tk]}
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed">{c[dk]}</p>
          </div>
        ))}
      </div>
    </Section>
  );
};

const HowItWorks = () => {
  const { lang } = useI18n();
  const c = copy[lang];
  const steps = [
    { t: c.how1t, d: c.how1d, icon: Wallet },
    { t: c.how2t, d: c.how2d, icon: Gauge },
    { t: c.how3t, d: c.how3d, icon: FileDown },
  ];
  return (
    <Section id="how" eyebrow={c.howEyebrow} title={c.howTitle}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={i}
              data-testid={`how-step-${i + 1}`}
              className="relative bg-white/70 backdrop-blur-xl border border-white/60 rounded-2xl p-7 overflow-hidden"
            >
              <span
                className="absolute -top-4 -right-4 text-[120px] leading-none text-indigo-50 font-bold select-none"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {i + 1}
              </span>
              <div className="relative z-10">
                <div className="w-10 h-10 rounded-xl bg-indigo-950 text-white flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-medium text-indigo-950 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {s.t}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

const Why = () => {
  const { lang } = useI18n();
  const c = copy[lang];
  return (
    <Section id="why" eyebrow={c.whyEyebrow} title={c.whyTitle}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {WHY.map(({ icon: Icon, tk, dk }) => (
          <div
            key={tk}
            data-testid={`why-${tk}`}
            className="flex items-start gap-5 bg-white/60 backdrop-blur-xl border border-white/60 rounded-2xl p-7"
          >
            <div className="w-12 h-12 rounded-xl bg-indigo-950 text-white flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-medium text-indigo-950 mb-1.5" style={{ fontFamily: "Outfit, sans-serif" }}>
                {c[tk]}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">{c[dk]}</p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

const PromiseSection = () => {
  const { lang } = useI18n();
  const c = copy[lang];
  const stats = [
    { v: c.promiseStat1, s: c.promiseStat1Sub },
    { v: c.promiseStat2, s: c.promiseStat2Sub },
    { v: c.promiseStat3, s: c.promiseStat3Sub },
  ];
  return (
    <section
      id="promise"
      data-testid="section-promise"
      className="relative bg-indigo-950 overflow-hidden py-24 md:py-32"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(50% 45% at 15% 15%, rgba(99,102,241,0.45), transparent 65%), radial-gradient(50% 40% at 85% 85%, rgba(16,185,129,0.28), transparent 65%), radial-gradient(40% 35% at 100% 15%, rgba(251,191,36,0.14), transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          maskImage: "radial-gradient(60% 50% at 50% 50%, #000 30%, transparent 80%)",
        }}
      />

      <div className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center">
        <span className="text-xs font-bold uppercase tracking-[0.28em] text-emerald-300/90">
          {c.promiseEyebrow}
        </span>
        <h2
          className="mt-5 text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.05] text-white"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {c.promiseTitle1}
          <br />
          <span className="bg-gradient-to-r from-emerald-300 via-emerald-400 to-amber-300 bg-clip-text text-transparent">
            {c.promiseTitleAccent}
          </span>
        </h2>
        <p className="mt-7 text-base md:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
          {c.promiseBody}
        </p>

        <div className="mt-14 grid grid-cols-3 gap-4 md:gap-10 max-w-3xl mx-auto">
          {stats.map((s, i) => (
            <div
              key={i}
              data-testid={`promise-stat-${i + 1}`}
              className="flex flex-col items-center gap-1"
            >
              <span
                className="text-4xl sm:text-5xl md:text-6xl font-medium tracking-tighter text-white"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {s.v}
              </span>
              <span className="text-xs md:text-sm text-white/55">{s.s}</span>
            </div>
          ))}
        </div>

        <div className="mt-14 flex justify-center">
          <Link
            to="/app"
            data-testid="promise-cta"
            className="group flex items-center gap-2 bg-white text-indigo-950 hover:bg-emerald-300 rounded-xl px-7 py-4 text-base font-semibold transition-all shadow-xl active:scale-95"
          >
            {c.promiseCta}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const { lang } = useI18n();
  const c = copy[lang];
  const items = [
    { q: c.t1q, n: c.t1n, r: c.t1r, co: c.t1c },
    { q: c.t2q, n: c.t2n, r: c.t2r, co: c.t2c },
    { q: c.t3q, n: c.t3n, r: c.t3r, co: c.t3c },
  ];
  return (
    <Section id="testimonials" eyebrow={c.testimonialsEyebrow} title={c.testimonialsTitle}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {items.map((it, i) => (
          <figure
            key={i}
            data-testid={`testimonial-${i + 1}`}
            className="flex flex-col gap-5 bg-white/70 backdrop-blur-xl border border-white/60 rounded-2xl p-7 transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(30,27,75,0.08)]"
          >
            <span
              aria-hidden
              className="text-5xl text-indigo-200 leading-none -mb-3"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              “
            </span>
            <blockquote className="text-base text-slate-700 leading-relaxed flex-1">
              {it.q}
            </blockquote>
            <figcaption className="flex items-center gap-3 pt-4 border-t border-slate-200/70">
              <div
                className="w-10 h-10 rounded-full bg-indigo-950 text-white flex items-center justify-center text-sm font-medium shrink-0"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {it.n.split(" ").map((p) => p[0]).join("")}
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-indigo-950">{it.n}</span>
                <span className="text-xs text-slate-500">
                  {it.r} · <span className="text-indigo-700/80 font-medium">{it.co}</span>
                </span>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </Section>
  );
};

const Faq = () => {
  const { lang } = useI18n();
  const c = copy[lang];
  const items = [
    { q: c.q1, a: c.a1 },
    { q: c.q2, a: c.a2 },
    { q: c.q3, a: c.a3 },
    { q: c.q4, a: c.a4 },
  ];
  const [open, setOpen] = useState(0);
  return (
    <Section id="faq" eyebrow={c.faqEyebrow} title={c.faqTitle}>
      <div className="flex flex-col gap-3 max-w-3xl">
        {items.map((it, i) => {
          const isOpen = open === i;
          return (
            <button
              key={i}
              data-testid={`faq-item-${i}`}
              onClick={() => setOpen(isOpen ? -1 : i)}
              className="text-left bg-white/70 backdrop-blur-xl border border-white/60 rounded-2xl p-6 hover:bg-white/90 transition-all"
            >
              <div className="flex items-start justify-between gap-4">
                <h3 className="text-base md:text-lg font-medium text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {it.q}
                </h3>
                <span
                  className={cn(
                    "w-7 h-7 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center shrink-0 transition-transform",
                    isOpen ? "rotate-45" : ""
                  )}
                >
                  <span className="text-xl leading-none">+</span>
                </span>
              </div>
              {isOpen ? (
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{it.a}</p>
              ) : null}
            </button>
          );
        })}
      </div>
    </Section>
  );
};

const FinalCta = () => {
  const { lang } = useI18n();
  const c = copy[lang];
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-20" data-testid="section-cta">
      <div className="relative rounded-3xl bg-indigo-950 text-white p-10 md:p-16 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(60% 50% at 80% 0%, rgba(16,185,129,0.35), transparent 60%), radial-gradient(50% 40% at 0% 100%, rgba(251,191,36,0.25), transparent 60%)",
          }}
        />
        <div className="relative z-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-2xl">
            <h2
              className="text-3xl md:text-5xl tracking-tighter leading-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {c.finalTitle}
            </h2>
            <p className="mt-4 text-white/75 text-base md:text-lg">{c.finalSubtitle}</p>
          </div>
          <Link
            to="/app"
            data-testid="final-cta"
            className="flex items-center gap-2 bg-emerald-400 hover:bg-emerald-300 text-indigo-950 rounded-xl px-7 py-4 text-base font-semibold transition-all shadow-lg active:scale-95 w-fit"
          >
            {c.ctaPrimary}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const { lang } = useI18n();
  const c = copy[lang];
  return (
    <footer className="border-t border-slate-200/70 bg-white/60 backdrop-blur-xl" data-testid="landing-footer">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-950 flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-white" />
            </div>
            <span className="text-base font-medium text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
              BudgetLens
            </span>
          </div>
          <p className="text-sm text-slate-500 max-w-sm">{c.footerTagline}</p>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 mb-1">
            {c.footerProduct}
          </span>
          <a href="#features" className="text-sm text-slate-600 hover:text-indigo-950">
            {c.navFeatures}
          </a>
          <a href="#how" className="text-sm text-slate-600 hover:text-indigo-950">
            {c.navHow}
          </a>
          <Link to="/app" className="text-sm text-slate-600 hover:text-indigo-950">
            {c.launch}
          </Link>
        </div>
      </div>
      <div className="border-t border-slate-200/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between text-xs text-slate-400">
          <span>© {new Date().getFullYear()} BudgetLens. {c.footerRights}</span>
          <span className="flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5" />
            Made for advisors
          </span>
        </div>
      </div>
    </footer>
  );
};

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50 relative overflow-x-hidden" data-testid="landing-page">
      <Navbar />
      <Hero />
      <Features />
      <HowItWorks />
      <Why />
      <PromiseSection />
      <Testimonials />
      <Faq />
      <FinalCta />
      <Footer />
    </div>
  );
}
